import { Injectable, inject } from '@angular/core';
import { TuiToastService } from '@taiga-ui/kit/components/toast';
import { type Observable } from 'rxjs';

type ToastKind = 'success' | 'error' | 'warning' | 'info';

const KINDS: Readonly<Record<ToastKind, { appearance: string; icon: string }>> = {
  success: { appearance: 'toast-success', icon: '@tui.shield-check' },
  error: { appearance: 'toast-error', icon: '@tui.shield-x' },
  warning: { appearance: 'toast-warning', icon: '@tui.shield-alert' },
  info: { appearance: 'toast-info', icon: '@tui.shield' },
};

const AUTO_CLOSE_MS: Readonly<Record<ToastKind, number>> = {
  error: 0,
  info: 4_000,
  success: 3_000,
  warning: 6_000,
};

interface ToastConfig {
  description?: string;
  autoClose?: number;
  closable?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toasts = inject(TuiToastService);

  success(message: string, config?: ToastConfig): void {
    this.fire('success', message, config);
  }

  error(message: string, config?: ToastConfig): void {
    this.fire('error', message, config);
  }

  warning(message: string, config?: ToastConfig): void {
    this.fire('warning', message, config);
  }

  info(message: string, config?: ToastConfig): void {
    this.fire('info', message, config);
  }

  show(message: string, tone: ToastKind, config?: ToastConfig): void {
    this.fire(tone, message, config);
  }

  open(tone: ToastKind, message: string, config: ToastConfig = {}): Observable<void> {
    const { appearance, icon } = KINDS[tone];
    const { description, autoClose = AUTO_CLOSE_MS[tone], closable = true } = config;

    return this.toasts.open<void>(contentOf(message, description), {
      appearance,
      data: icon,
      autoClose,
      closable,
    });
  }

  private fire(tone: ToastKind, message: string, config?: ToastConfig): void {
    this.open(tone, message, config).subscribe();
  }
}

function contentOf(message: string, description: string | undefined): string {
  if (!description) {
    return message;
  }

  return `<span class="toast__copy"><span class="toast__title">${escapeHtml(message)}</span><span class="toast__description">${escapeHtml(description)}</span></span>`;
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
